import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Bot,
  Briefcase,
  Building2,
  Check,
  ChevronRight,
  Clock,
  FileText,
  Globe,
  MessageSquare,
  Send,
  Shield,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import SiteNavBar from '@/components/SiteNavBar';

// Animation component (matches FlyAgent style)
const RevealOnScroll = ({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTimeout(() => setIsVisible(true), delay * 100); },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${className}`}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(24px)' }}>
      {children}
    </div>
  );
};

const ParticleField = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(30)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 rounded-full bg-violet-500/30"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
        50% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
      }
    `}</style>
  </div>
);

const CONTACT_EMAILS = ['manu.mani@zoomlocal.in', 'anand.mahajan@zoomlocal.in', 'kedar.deshmukh@zoomlocal.in'];

const AgencyContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    locations: '1-10',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-managed-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          ...formData,
          to: CONTACT_EMAILS,
        }),
      });

      if (!response.ok) throw new Error('Failed to send');
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('[AgenciesPage] Contact form error:', err);
      setError('Something went wrong. Please try again or reach us on WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-violet-400" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
        <p className="text-gray-400 max-w-md mx-auto">
          Our team will review your inquiry and get back to you within 24 hours to discuss the best setup for your agency.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Your Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/25 transition-colors"
            placeholder="Your Name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/25 transition-colors"
            placeholder="you@agency.com"
          />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/25 transition-colors"
            placeholder="+91 98765 43210"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Agency / Business Name *</label>
          <input
            type="text"
            required
            value={formData.businessName}
            onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/25 transition-colors"
            placeholder="Your Agency Name"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Number of Client Locations</label>
        <select
          value={formData.locations}
          onChange={(e) => setFormData({ ...formData, locations: e.target.value })}
          className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/25 transition-colors"
        >
          <option value="1-10">1–10 locations</option>
          <option value="11-50">11–50 locations</option>
          <option value="51-100">51–100 locations</option>
          <option value="100+">100+ locations</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Message</label>
        <textarea
          rows={3}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/25 transition-colors resize-none"
          placeholder="Tell us about your agency, clients, and what you're looking for..."
        />
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <Button
        type="submit"
        size="lg"
        disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white rounded-xl py-6 text-lg"
      >
        {isSubmitting ? 'Sending...' : 'Get in Touch'}
        <Send className="w-5 h-5 ml-2" />
      </Button>
    </form>
  );
};

declare global {
  interface Window {
    __GEO_COUNTRY__?: string;
  }
}

const AgenciesPage = () => {
  const [isIndianUser, setIsIndianUser] = useState(true);

  useEffect(() => {
    const VERCEL_EDGE_REGIONS = ['SG', 'HK', 'JP', 'KR', 'AU', 'NZ'];

    const detectCountry = async () => {
      const geoCountry = typeof window !== 'undefined' ? window.__GEO_COUNTRY__ : undefined;

      if (geoCountry && VERCEL_EDGE_REGIONS.includes(geoCountry)) {
        try {
          const response = await fetch('/api/geo');
          const data = await response.json();
          if (data.countryCode && data.countryCode !== 'IN') setIsIndianUser(false);
          return;
        } catch { return; }
      }

      if (geoCountry && geoCountry !== 'IN') setIsIndianUser(false);
    };

    detectCountry();
  }, []);

  return (
    <>
      <Helmet>
        <title>Fly for Agencies | AI-Powered Local SEO at Scale</title>
        <meta name="description" content="Manage all your clients' Google Business Profiles through AI. 14 specialized agents for reviews, rankings, content, and reporting. Works with Claude, ChatGPT, and Gemini." />
        <meta property="og:title" content="Fly for Agencies | Local SEO at Scale" />
        <meta property="og:description" content="AI agents that handle GBP management for your entire client roster. Save 10+ hours per week." />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-white">
        <SiteNavBar />

        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          <ParticleField />

          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '64px 64px',
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-b from-violet-950/20 via-transparent to-gray-950" />

          <div className="relative z-10 container mx-auto px-4 text-center">
            <RevealOnScroll>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-8">
                <Building2 className="w-4 h-4 text-violet-400" />
                <span className="text-sm text-violet-300">Built for Marketing Agencies & Freelancers</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400">
                  Scale Local SEO
                </span>
                <br />
                <span className="text-white">With AI Agents</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-8 leading-relaxed">
                Two powerful ways to manage your clients' Local SEO —
                <span className="text-violet-400"> Fly's Web Dashboard</span> with white-label workspaces for your entire roster, or
                <span className="text-fuchsia-400"> Fly Agent</span> with 14 AI agents that work through natural conversation.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <a href="#contact">
                  <Button size="lg" className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25">
                    <Send className="w-5 h-5 mr-2" />
                    Get in Touch
                  </Button>
                </a>
                <Link to="/fly-agent">
                  <Button size="lg" className="bg-transparent border border-gray-600 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl">
                    <Bot className="w-5 h-5 mr-2" />
                    See All 14 Agents
                  </Button>
                </Link>
              </div>

              <p className="text-sm text-gray-500">Web DIY Dashboard + AI Agents via Claude (MCP), ChatGPT (Actions), and Gemini (OpenAPI)</p>
            </RevealOnScroll>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronRight className="w-6 h-6 text-gray-600 rotate-90" />
          </div>
        </section>

        {/* Why Agencies Choose Fly */}
        <section className="py-24 relative bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                  Why Agencies Choose Fly
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Stop drowning in client dashboards. Let AI handle the repetitive work while you focus on strategy.
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {[
                {
                  icon: Clock,
                  stat: '10+',
                  unit: 'hrs/week',
                  label: 'Time Saved',
                  description: 'Automate review responses, reporting, and profile monitoring across all clients',
                  color: '#8b5cf6',
                },
                {
                  icon: Users,
                  stat: '50+',
                  unit: 'clients',
                  label: 'Scale Effortlessly',
                  description: 'Manage unlimited client profiles from a single AI conversation',
                  color: '#ec4899',
                },
                {
                  icon: FileText,
                  stat: '1-Click',
                  unit: '',
                  label: 'Branded Reports',
                  description: 'Professional PDF reports with your agency branding, generated instantly',
                  color: '#06b6d4',
                },
                {
                  icon: Zap,
                  stat: '14',
                  unit: 'agents',
                  label: 'AI at Your Command',
                  description: 'Specialized agents for every aspect of GBP management',
                  color: '#f59e0b',
                },
              ].map((item, i) => (
                <RevealOnScroll key={i} delay={i}>
                  <div className="relative group h-full">
                    <div className="absolute -inset-0.5 rounded-2xl blur opacity-0 group-hover:opacity-40 transition-opacity duration-500"
                      style={{ background: `linear-gradient(135deg, ${item.color}40, transparent)` }} />
                    <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-gray-700 transition-all duration-300 p-6 h-full">
                      <item.icon className="w-8 h-8 mb-4" style={{ color: item.color }} />
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-3xl font-black text-white">{item.stat}</span>
                        <span className="text-sm text-gray-500">{item.unit}</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-300 mb-2">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.description}</p>
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* What You Can Do */}
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-violet-950/10 to-gray-950" />

          <div className="relative container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400">
                    One Conversation, Endless Capabilities
                  </span>
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Ask your AI assistant anything about your clients' Google profiles
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {[
                {
                  icon: Star,
                  title: 'Review Management',
                  description: 'AI-powered responses, auto-responder, sentiment analysis, and review monitoring across all clients.',
                  color: '#f59e0b',
                  gradient: 'from-amber-500 to-orange-400',
                },
                {
                  icon: TrendingUp,
                  title: 'Rank Tracking',
                  description: 'Track keyword rankings across Google Maps for every client. Geographic grid analysis and competitor tracking.',
                  color: '#3b82f6',
                  gradient: 'from-blue-500 to-cyan-400',
                },
                {
                  icon: Shield,
                  title: 'Profile Protection',
                  description: 'Monitor all client profiles for unauthorized changes. Get alerts for category, hours, and contact info edits.',
                  color: '#10b981',
                  gradient: 'from-emerald-500 to-teal-400',
                },
                {
                  icon: MessageSquare,
                  title: 'Content Publishing',
                  description: 'AI generates engaging posts, schedules them, and publishes across client profiles with consistent brand voice.',
                  color: '#ec4899',
                  gradient: 'from-pink-500 to-rose-400',
                },
                {
                  icon: FileText,
                  title: 'Branded Reporting',
                  description: 'One-click professional reports with your agency logo. Monthly summaries, PDF export, and auto-email delivery.',
                  color: '#6366f1',
                  gradient: 'from-indigo-500 to-violet-400',
                },
                {
                  icon: Globe,
                  title: 'Microsite Builder',
                  description: 'Create beautiful landing pages and store locators for clients. Custom domains and auto-generated SEO content.',
                  color: '#14b8a6',
                  gradient: 'from-teal-500 to-emerald-400',
                },
              ].map((item, index) => (
                <RevealOnScroll key={index} delay={index}>
                  <div className="relative group h-full">
                    <div className="absolute -inset-0.5 rounded-2xl blur-lg opacity-0 group-hover:opacity-40 transition-opacity duration-500"
                      style={{ background: `linear-gradient(135deg, ${item.color}40, transparent)` }} />
                    <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-gray-700 transition-all duration-300 p-6 h-full">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.gradient} p-2.5 mb-4 shadow-lg`}
                        style={{ boxShadow: `0 8px 32px ${item.color}40` }}
                      >
                        <item.icon className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Works With */}
        <section className="py-24 relative bg-gradient-to-b from-gray-950 via-violet-950/10 to-gray-950">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Bring Your Own AI</h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Fly Agent integrates with the AI platforms your team already uses
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { name: 'Claude', protocol: 'MCP Protocol', color: '#f97316', link: '/fly-agent/guides/claude' },
                { name: 'ChatGPT', protocol: 'GPT Actions', color: '#10b981', link: '/fly-agent/guides/chatgpt' },
                { name: 'Gemini', protocol: 'OpenAPI', color: '#3b82f6', link: '/fly-agent/guides/gemini' },
              ].map((ai, index) => (
                <RevealOnScroll key={index} delay={index}>
                  <Link to={ai.link} className="block h-full group">
                    <div className="relative h-full">
                      <div className="absolute -inset-0.5 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ background: `linear-gradient(135deg, ${ai.color}30, transparent)` }} />
                      <div className="relative h-full bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 p-8 text-center hover:border-gray-700 transition-all duration-300">
                        <div className="w-16 h-16 rounded-2xl p-3 mb-6 mx-auto" style={{ backgroundColor: `${ai.color}20` }}>
                          <Bot className="w-full h-full" style={{ color: ai.color }} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">{ai.name}</h3>
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4"
                          style={{ backgroundColor: `${ai.color}15`, color: ai.color, borderColor: `${ai.color}30`, borderWidth: '1px' }}>
                          {ai.protocol}
                        </div>
                        <div className="flex items-center justify-center text-sm font-medium group-hover:translate-x-1 transition-transform" style={{ color: ai.color }}>
                          View Setup Guide <ArrowRight className="w-4 h-4 ml-2" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-24 relative">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Agency Pricing</h2>
                <p className="text-gray-400 text-lg mb-12">Full access to all 14 AI agents. Volume discounts available.</p>

                <div className="relative inline-block">
                  <div className="absolute -inset-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-3xl blur-lg opacity-50" />
                  <div className="relative bg-gray-900 rounded-3xl border border-gray-800 p-8 md:p-12">
                    <div className="flex items-baseline justify-center gap-2 mb-2">
                      <span className="text-5xl md:text-6xl font-black text-white">{isIndianUser ? '₹999' : '$14.99'}</span>
                      <span className="text-gray-400 text-lg">/location/month</span>
                    </div>
                    <p className="text-gray-500 mb-8">Billed monthly. Cancel anytime. Multi-location discounts available.</p>

                    <ul className="space-y-4 text-left max-w-sm mx-auto mb-8">
                      {[
                        'All 14 AI agents included',
                        'Works with Claude, ChatGPT & Gemini',
                        'Unlimited API calls',
                        'White-label branded reports',
                        'Multi-workspace management',
                        'Priority support',
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-gray-300">
                          <Check className="w-5 h-5 text-violet-400 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <a href="#contact">
                      <Button size="lg" className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white px-8 py-6 text-lg rounded-xl w-full">
                        Get in Touch
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        {/* Contact / CTA */}
        <section id="contact" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-950/50 via-fuchsia-950/50 to-cyan-950/50" />
          <ParticleField />

          <div className="relative container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <RevealOnScroll>
                <div className="text-center mb-10">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                    Ready to Scale Your Agency?
                  </h2>
                  <p className="text-lg text-gray-400">
                    Tell us about your agency and we'll help you pick the right setup — Web Dashboard, AI Agents, or both.
                  </p>
                </div>

                <div className="bg-gray-900/80 backdrop-blur-sm rounded-3xl border border-gray-800 p-8 md:p-10">
                  <AgencyContactForm />
                </div>

                {/* WhatsApp Alternative */}
                <div className="mt-8 text-center">
                  <p className="text-gray-500 text-sm mb-4">Or reach us directly on WhatsApp</p>
                  <a
                    href="https://wa.me/918446712833?text=Hi%2C%20I%27m%20a%20marketing%20agency%20interested%20in%20Fly%20for%20managing%20my%20clients%27%20Local%20SEO"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gray-900/80 backdrop-blur-sm border border-gray-800 hover:border-violet-500/50 transition-all duration-300 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-violet-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-semibold text-sm group-hover:text-violet-400 transition-colors">Chat on WhatsApp</p>
                      <p className="text-gray-500 text-xs">+91 84467 12833</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-violet-400 group-hover:translate-x-1 transition-all" />
                  </a>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </section>

        <Footer variant="dark" />
      </div>
    </>
  );
};

export default AgenciesPage;
